/**
 * Language-aware transforms for admin preview payloads.
 * Flatten localizedContent onto top-level fields so preview pages can render
 * without guessing the language from titles.
 */

const ASSIGNMENT_FIELDS = new Set([
  'listen',
  'complete',
  'continueLearning',
  'listenLabel',
  'completeLabel',
  'continueLearningLabel',
])

const UNIT_TEXT_FIELDS = [
  'title',
  'subtitle',
  'description',
  'listen',
  'listenLabel',
  'complete',
  'completeLabel',
  'continueLearning',
  'continueLearningLabel',
  'truthBodyContent',
  'truthInBible',
  'equipBodyContent',
  'accountBodyContent',
  'footnotesBodyContent',
  'copyrightBodyContent',
]

const UNIT_ARRAY_FIELDS = [
  'truthVersesQuotes',
  'truthStudyQuestions',
  'equipStudyQuestions',
  'accountStudyQuestions',
]

function normalizeLang(languageCode) {
  return String(languageCode || 'en').toLowerCase()
}

function isEmptyHtml(value) {
  if (value === undefined || value === null || value === '') return true
  return String(value).replace(/<[^>]*>/g, '').trim() === ''
}

export function parsePreviewArgs(idOrOpts, fallbackLang = 'en') {
  if (idOrOpts && typeof idOrOpts === 'object' && !Array.isArray(idOrOpts)) {
    return {
      id: idOrOpts.id || null,
      lang: normalizeLang(idOrOpts.lang || fallbackLang),
    }
  }
  return {
    id: idOrOpts || null,
    lang: normalizeLang(fallbackLang),
  }
}

export function transformUnitForLanguage(unit, languageCode = 'en') {
  if (!unit) return unit

  const lang = normalizeLang(languageCode)
  const localizedData = unit.localizedContent?.[lang] || null

  const getLangValue = (key) => {
    if (localizedData && key in localizedData) {
      const langValue = localizedData[key]
      if (ASSIGNMENT_FIELDS.has(key) && isEmptyHtml(langValue)) return ''
      return langValue || ''
    }
    if (lang === 'en') return unit[key] || ''
    return ''
  }

  const getLangArray = (key) => {
    const langArray = localizedData?.[key]
    if (Array.isArray(langArray) && langArray.length > 0) return langArray
    if (lang === 'en') return unit[key] || []
    return []
  }

  const transformed = {
    id: unit.id,
    category: unit.category,
    status: unit.status,
    type: unit.type,
    cid: unit.cid,
    verified: unit.verified,
    updated: unit.updated,
    completionScore: unit.completionScore,
    previewLang: lang,
    localizedContent: unit.localizedContent || {},
  }

  UNIT_TEXT_FIELDS.forEach((key) => {
    transformed[key] = getLangValue(key)
  })
  UNIT_ARRAY_FIELDS.forEach((key) => {
    transformed[key] = getLangArray(key)
  })

  return transformed
}

export function transformWeekForLanguage(week, languageCode = 'en') {
  if (!week) return week

  const lang = normalizeLang(languageCode)
  const localizedData = week.localizedContent?.[lang] || null
  const legacyPt = lang === 'pt' ? week.pt : null

  const getLangValue = (key) => {
    const langValue = localizedData?.[key]
    if (langValue !== undefined && langValue !== null && langValue !== '') {
      return langValue
    }
    if (legacyPt?.[key]) return legacyPt[key]
    if (lang === 'en') return week[key] || ''
    return ''
  }

  const getLangArray = (key) => {
    const langValue = localizedData?.[key]
    if (Array.isArray(langValue) && langValue.length > 0) return langValue
    if (Array.isArray(legacyPt?.[key]) && legacyPt[key].length > 0) {
      return legacyPt[key]
    }
    if (lang === 'en' && Array.isArray(week[key])) return week[key]
    return []
  }

  return {
    id: week.id,
    category: week.category,
    status: week.status,
    type: week.type,
    cid: week.cid,
    verified: week.verified,
    previewLang: lang,
    title: getLangValue('title'),
    subtitle: getLangValue('subtitle'),
    missionBodyContent: getLangValue('missionBodyContent'),
    supplicationBodyContent: getLangValue('supplicationBodyContent'),
    missionStudyQuestions: getLangArray('missionStudyQuestions'),
    supplicationStudyQuestions: getLangArray('supplicationStudyQuestions'),
    supplicationVerseQuotes: getLangArray('supplicationVerseQuotes'),
  }
}

export function collectRefIds(refs) {
  if (!Array.isArray(refs)) return []
  return refs
    .map((ref) => {
      if (!ref) return null
      if (typeof ref === 'string') return ref
      return ref.id || null
    })
    .filter(Boolean)
}
